import React, { useState } from 'react';
import { useStoreState, useStoreActions } from 'easy-peasy';
import { useTheme } from '@mui/material/styles';
import {
	Stack,
	Box,
	Typography,
	IconButton,
	Tooltip,
	Popover,
	FormGroup,
	FormControlLabel,
	Switch,
	Button,
	CircularProgress
} from '@mui/material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import MicExternalOnIcon from '@mui/icons-material/MicExternalOn';
import CloseIcon from '@mui/icons-material/Close';
import utils from 'components/utils';
import moment from 'moment';

const SpecialUpdate = props => {
	const { item } = props;
	const theme = useTheme();
	const translate = utils.useI18N();
	const loadingUpdate = useStoreState(state => state.wishlist.list.loadingUpdate);
	const updateSpecial = useStoreActions(dispatch => dispatch.wishlist.list.updateSpecial);
	const [anchorEl, setAnchorEl] = useState(null);
	const open = Boolean(anchorEl);
	const [special, setSpecial] = useState(item.special);
	const [eventDate, setEventDate] = useState(item.eventDate ? moment(item.eventDate) : null);
	const [deliveryDate, setDeliveryDate] = useState(item.deliveryDate ? moment(item.deliveryDate) : null);
	const eventDateBefore = eventDate && moment(eventDate).diff(moment(), 'days') < 14;
	const eventDatePast = eventDate && moment(eventDate).diff(moment(), 'days') < 0;
	const deliveryDatePast = deliveryDate && moment(deliveryDate).diff(moment(), 'days') < 0;
	const isInvalidEventDate = eventDate && moment(eventDate).format('DD/MM/YYYY').length === 1;
	const isInvalidDeliveryDate =
		deliveryDate &&
		(moment(deliveryDate).format('DD/MM/YYYY').length === 1 ||
			moment(eventDate).diff(moment(deliveryDate), 'days') < 0);
	const isDisable =
		special && (!eventDate || isInvalidEventDate || eventDatePast || isInvalidDeliveryDate || deliveryDatePast);

	const handleOpen = event => {
		event.stopPropagation();
		setAnchorEl(event.currentTarget);
	};

	const handleClose = event => {
		event.stopPropagation();
		setAnchorEl(null);
	};

	const handelSpecial = event => {
		setSpecial(event.target.checked);
	};

	const onClick = event => {
		const convertEventDate = special && eventDate ? moment(eventDate).format('YYYY-MM-DD') : null;
		const convertDeliveryDate = special && deliveryDate ? moment(deliveryDate).format('YYYY-MM-DD') : null;
		updateSpecial({
			wishlistId: item.id,
			name: item.name,
			type: item.type,
			note: item.note,
			special: special,
			eventDate: convertEventDate,
			deliveryDate: convertDeliveryDate
		});
	};

	return (
		<Box>
			<Tooltip title={translate('wishlist.eventDate') + ':' + moment(item.eventDate).format('L')}>
				<IconButton onClick={handleOpen}>
					<MicExternalOnIcon sx={{ color: theme.palette.custom.wishlistSpecial }} />
				</IconButton>
			</Tooltip>
			<Popover
				open={open}
				anchorEl={anchorEl}
				onClose={handleClose}
				anchorOrigin={{
					vertical: 'center',
					horizontal: 'left'
				}}
				transformOrigin={{
					vertical: 'center',
					horizontal: 'right'
				}}
			>
				<Stack
					direction="column"
					spacing={2}
					sx={{
						width: '340px',
						padding: '10px'
					}}
				>
					<Stack
						direction="row"
						justifyContent="flex-start"
						alignItems="center"
						spacing={2}
						sx={{ width: '100%', borderBottom: `1px solid ${theme.palette.primary.main}` }}
					>
						<Box sx={{ width: '90%' }}>
							<Typography variant="titleSection" sx={{ color: theme.palette.primary.main }}>
								{translate('wishlist.specialWishlist')}
							</Typography>
						</Box>
						<Box sx={{ width: '10%' }}>
							<IconButton onClick={handleClose}>
								<CloseIcon />
							</IconButton>
						</Box>
					</Stack>
					<Stack
						direction="column"
						justifyContent="flex-start"
						alignItems="flex-start"
						spacing={1}
						sx={{ width: '100%' }}
					>
						<Stack
							direction="row"
							justifyContent="space-between"
							alignItems="center"
							spacing={0}
							sx={{ width: '100%' }}
						>
							<FormGroup>
								<FormControlLabel
									label={
										<Typography variant="text" sx={{ textTransform: 'capitalize' }}>
											{translate('wishlist.special')}
										</Typography>
									}
									control={<Switch checked={special} onChange={handelSpecial} />}
								/>
							</FormGroup>
							<Typography variant="titleTab">{item.type}</Typography>
						</Stack>
						<Stack
							direction="row"
							justifyContent="space-between"
							alignItems="flex-start"
							spacing={1}
							sx={{ width: '100%' }}
						>
							<LocalizationProvider dateAdapter={AdapterMoment}>
								<DatePicker
									disablePast
									value={eventDate}
									disabled={!special}
									format="DD/MM/YYYY"
									onChange={date => setEventDate(date)}
									label={translate('wishlist.eventDate')}
									slotProps={{
										textField: {
											size: 'small',
											helperText:
												isInvalidEventDate || eventDatePast ? (
													<Typography
														variant="text"
														sx={{ fontSize: '12px', color: theme.palette.custom.red }}
													>
														{translate('wishlist.eventDateError')}
													</Typography>
												) : eventDateBefore ? (
													<Typography
														variant="text"
														sx={{ fontSize: '12px', color: theme.palette.custom.orange }}
													>
														{translate('wishlist.specialDateBefore')}
													</Typography>
												) : !eventDate ? (
													<Typography variant="text" sx={{ fontSize: '12px' }}>
														{translate('wishlist.specialDateDesc')}
													</Typography>
												) : (
													<Typography variant="text" sx={{ fontSize: '12px' }}>
														{translate('wishlist.eventDate')}
													</Typography>
												)
										}
									}}
								/>
								<DatePicker
									disablePast
									value={deliveryDate}
									disabled={!special}
									format="DD/MM/YYYY"
									onChange={date => setDeliveryDate(date)}
									label={translate('wishlist.deliveryDate')}
									slotProps={{
										textField: {
											size: 'small',
											helperText:
												isInvalidDeliveryDate || deliveryDatePast ? (
													<Typography
														variant="text"
														sx={{ fontSize: '12px', color: theme.palette.custom.red }}
													>
														{translate('wishlist.deliveryDateError')}
													</Typography>
												) : !deliveryDate ? (
													<Typography variant="text" sx={{ fontSize: '12px' }}>
														{translate('wishlist.specialDateDeliveryDesc')}
													</Typography>
												) : (
													<Typography variant="text" sx={{ fontSize: '12px' }}>
														{translate('wishlist.deliveryDate')}
													</Typography>
												)
										}
									}}
								/>
							</LocalizationProvider>
						</Stack>
					</Stack>
					<Stack
						direction="row"
						justifyContent="space-between"
						alignItems="center"
						spacing={2}
						sx={{ width: '100%', marginTop: '10px !important' }}
					>
						<Button variant="dark" disabled={isDisable} onClick={onClick}>
							{loadingUpdate ? (
								<CircularProgress size={24} sx={{ color: theme.palette.custom.white }} />
							) : (
								translate('common.save')
							)}
						</Button>
						<Button variant="light" onClick={handleClose}>
							{translate('common.cancel')}
						</Button>
					</Stack>
				</Stack>
			</Popover>
		</Box>
	);
};

export default SpecialUpdate;
